import { useCallback, useEffect, useRef } from 'react';
import { useBreakpoints } from '../../../../bookings/util/useBreakpoints';
import { LinkId } from '../../../AnalyticsContext/LinkId';
import { TrackingDataEvent } from '../../../AnalyticsContext/LinkTracking';
import { applyTrackingId } from '../../../AnalyticsContext/applyTrackingId';
import { renderLinkTrackingAttributes } from '../../../AnalyticsContext/renderLinkTrackingAttributes';
import { useHomepageTiles } from '../../../Experiments/HomepageTilesExperiment';
import { useEscapeKeyHandler } from '../../../hooks/keyboard/useEscapeKeyHandler';
import { useChangeEvent } from '../../../hooks/useChangeEvent';
import {
  Bar,
  CrossIcon,
  Input,
  SearchBarView,
  SearchIcon,
  SearchIconBackground,
} from './SearchBar.styles';

export interface Props {
  className?: string;
  value: string;
  onChange?: Setter<string>;
  setFocused?: Setter<boolean>;
  focused?: boolean;
  onClickSearch?: VoidFunction;
  onClear?: VoidFunction;
  inputRef?: React.RefObject<HTMLInputElement>;
  placeholder?: string;
  inline?: boolean;
}

const DEFAULT_PLACEHOLDER = 'Specialty, name or topic';

export const SearchBar = ({
  className,
  value,
  onChange,
  setFocused,
  focused = false,
  onClickSearch,
  onClear,
  inputRef,
  placeholder,
  inline,
}: Props) => {
  const mobile = useBreakpoints().Small;
  const homepageTilesExperiment = useHomepageTiles();

  // Disable auto focus on mobile and search value is empty
  const autoFocusRef = useRef(mobile && value.length === 0 ? false : true);

  const localRef = useRef<HTMLInputElement>(null);
  const ref = inputRef || localRef;

  /** When the search bar transitions to 'focused', the field should acquire a cursor, and vice-versa */
  useEffect(() => {
    if (!autoFocusRef.current) return;

    if (focused) {
      ref.current?.focus();
    } else {
      ref.current?.blur();
    }
  }, [focused, ref]);

  const observedPlaceholder = placeholder ?? DEFAULT_PLACEHOLDER;

  useEscapeKeyHandler(() => setFocused?.(false));

  const handleInputKeyDown = useCallback(() => {
    autoFocusRef.current = true;

    if (!focused) {
      setFocused?.(true);
    }
  }, [focused, setFocused]);

  const handleChange = useChangeEvent(onChange);

  const handleFocus = useCallback(() => {
    autoFocusRef.current = true;

    if (!inline) setFocused?.(true);
  }, [inline, setFocused]);

  const inlineFocussed = !!(value && inline);

  return (
    <SearchBarView className={className}>
      <Bar>
        <Input
          ref={ref}
          {...applyTrackingId(LinkId.HEADER_SEARCH_BAR)}
          onChange={handleChange}
          placeholder={observedPlaceholder}
          onFocus={handleFocus}
          value={value}
          onKeyDown={handleInputKeyDown}
          data-testid="searchBarInput"
          inlineFocus={inlineFocussed}
        />
        {value && <CrossIcon onClick={onClear} />}
        {(!mobile || (mobile && !focused && !value)) && (
          <SearchIconBackground
            onClick={onClickSearch}
            altTheme={homepageTilesExperiment}
            {...renderLinkTrackingAttributes({ event: TrackingDataEvent.SEARCH_BAR_SEARCH_ICON })}
          >
            <SearchIcon name="Search" />
          </SearchIconBackground>
        )}
      </Bar>
    </SearchBarView>
  );
};
